import { WalterApi } from "../walter/api";
import { JSONSchema9 } from "../types/jsonSchema";

export interface AiCodingResult {
  originalText: string;
  replacementText: string;
  reasoning: string;
  pathToValue: string;
  surroundingText: string;
}

export interface AiCodingStatus {
  status: "completed" | "failed" | "cancelled";
  items: AiCodingResult[];
}

export class AiCodingApi {
  private static readonly TIMEOUT_MS = 300_000; // 5 minutes
  private apiAbortControllers: AbortController[] = [];

  constructor(private abortSignal?: AbortSignal) {
    // Set up a listener to abort all API requests when the main signal is aborted
    if (this.abortSignal) {
      this.abortSignal.addEventListener("abort", () => {
        this.abortAllRequests();
      });
    }
  }

  private abortAllRequests() {
    // Abort all API requests that are in progress
    this.apiAbortControllers.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    // Clear the array
    this.apiAbortControllers = [];
  }

  async createAndPollAiCoding(
    schema: JSONSchema9,
    contents: string,
    definedRootRefs: string[] = [],
  ): Promise<AiCodingStatus> {
    if (this.abortSignal?.aborted) {
      return { status: "cancelled", items: [] };
    }

    const id = await this.createAiCoding(schema, contents, definedRootRefs);
    return this.pollForResult(id);
  }

  async createAiCoding(schema: JSONSchema9, contents: string, definedRootRefs: string[]): Promise<number> {
    if (this.abortSignal?.aborted) {
      throw new Error("Operation aborted");
    }

    const { response: createResponse, abortController } = WalterApi.createAiCoding(schema, contents, definedRootRefs);

    // Store the controller so we can abort it if needed
    this.apiAbortControllers.push(abortController);

    try {
      const resolvedResponse = await createResponse;
      if (!resolvedResponse) {
        throw new Error(`Failed to create AI coding`);
      }
      return resolvedResponse.id;
    } catch (error) {
      if (this.abortSignal?.aborted) {
        throw new Error("Operation aborted");
      }
      throw error;
    } finally {
      // Remove the controller from our list once the request completes
      const index = this.apiAbortControllers.indexOf(abortController);
      if (index > -1) {
        this.apiAbortControllers.splice(index, 1);
      }
    }
  }

  async pollForResult(id: number): Promise<AiCodingStatus> {
    if (this.abortSignal?.aborted) {
      return { status: "cancelled", items: [] };
    }

    const startTime = Date.now();
    let isPolling = true;

    while (isPolling) {
      if (this.abortSignal?.aborted) {
        return { status: "cancelled", items: [] };
      }

      if (Date.now() - startTime > AiCodingApi.TIMEOUT_MS) {
        throw new Error(`AI coding timed out`);
      }

      const { response: statusResponse, abortController } = WalterApi.getAiCoding(id);
      this.apiAbortControllers.push(abortController);

      try {
        const status = await statusResponse;

        if (status.status === "failed") {
          throw new Error(`AI coding failed`);
        }

        if (status.status === "completed") {
          isPolling = false;
          return status as AiCodingStatus;
        }

        // Wait before polling again
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        if (this.abortSignal?.aborted) {
          return { status: "cancelled", items: [] };
        }
        throw error;
      } finally {
        // Remove the controller from our list once the request completes
        const index = this.apiAbortControllers.indexOf(abortController);
        if (index > -1) {
          this.apiAbortControllers.splice(index, 1);
        }
      }
    }

    return { status: "completed", items: [] };
  }
}
